package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.textDirection
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LKbd
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun ProposalAssistItem(
    state: TranslationEditorState,
    row: KeyRow,
    result: EditorAssistResult.Proposal,
) {
    val proposal = result.proposal
    val actionKey = EditorProposalActionKey(
        projectSlug = state.projectSlug,
        locale = state.locale,
        keyId = row.key.id,
        proposalId = proposal.id,
    )
    val pending = state.proposalActions.pending[actionKey]
    val accepting = pending?.claim?.accept == true
    val rejecting = pending?.claim?.accept == false
    val provenance = t("editor.editor_assist_proposal_result.agent_proposal", "Agent proposal")
    // "by" is an English preposition welded between a translated noun and a name; in a language
    // that puts the agent first it cannot be moved from here.
    val actionProvenance = proposal.agentName?.takeIf(String::isNotBlank)
        ?.let { agentName ->
            // `tf` rather than two `replace` calls. Chained replaces substitute into each other's
            // output, so a provenance containing `{agent}` — or an agent named `{provenance}` —
            // gets read a second time; `tf` scans the message once and never rereads what it wrote.
            tf(
                "editor.editor_assist_proposal_result.provenance_by_agent",
                "{provenance} by {agent}",
                "provenance" to provenance,
                "agent" to agentName,
            )
        }
        ?: provenance

    Li(
        Modifier
            .attr("data-editor-assist-result", "true")
            .attr("data-editor-assist-ordinal", result.shortcutNumber.toString())
            .attr("data-editor-ranked-kind", "proposal")
            .toAttrs(),
    ) {
        Div(Modifier.attr("data-editor-assist-result-header", "true").toAttrs()) {
            AssistOrdinal(result.shortcutNumber)
            Span(Modifier.attr("data-editor-assist-provenance", "proposal").toAttrs()) {
                Text(provenance)
            }
            proposal.agentName?.takeIf(String::isNotBlank)?.let { agentName ->
                Span(Modifier.attr("data-editor-assist-agent-name", "true").toAttrs()) {
                    Text(agentName)
                }
            }
            LBadge(
                text = when {
                    accepting -> t("editor.editor_assist_proposal_result.accepting", "Accepting…")
                    rejecting -> t("editor.editor_assist_proposal_result.rejecting", "Rejecting…")
                    proposal.stale -> t("editor.editor_assist_proposal_result.source_changed", "Source changed")
                    else -> t("editor.editor_assist_proposal_result.ready_to_review", "Ready to review")
                },
                variant = when {
                    proposal.stale -> LBadgeVariant.WARNING
                    accepting || rejecting -> LBadgeVariant.INFO
                    else -> LBadgeVariant.NEUTRAL
                },
                modifier = Modifier.attr("data-editor-assist-status", "true"),
            )
            // **The cheapest triage a reviewer has**, in the one place they are already reading.
            // `unsure` is deliberately quiet rather than alarming: it is the agent being honest.
            proposal.confidence?.takeIf(String::isNotBlank)?.let { confidence ->
                LBadge(
                    text = when (confidence) {
                        "high" -> t("editor.editor_assist_proposal_result.confident", "confident")
                        "low" -> t("editor.editor_assist_proposal_result.unsure", "unsure")
                        else -> t("editor.editor_assist_proposal_result.fairly_sure", "fairly sure")
                    },
                    variant = when (confidence) {
                        "high" -> LBadgeVariant.SUCCESS
                        "low" -> LBadgeVariant.NEUTRAL
                        else -> LBadgeVariant.INFO
                    },
                    modifier = Modifier.attr("data-editor-assist-confidence", confidence),
                )
            }
        }
        Div(
            Modifier
                .attr("data-editor-assist-value", "true")
                .attr("data-editor-proposal-value", "true")
                .attr("lang", state.locale)
                .attr("dir", textDirection(state.locale).html)
                .toAttrs(),
        ) {
            Text(
                proposal.value ?: proposal.pluralForms.orEmpty().entries.joinToString(" · ") { (category, form) ->
                    // The label and the separator are both part of the message, as they already are
                    // in the history panel two clicks away (`editor.editor_models.history_form`).
                    // This was a Kotlin template nested inside a lambda inside `Text(...)`, which
                    // the extractor cannot reach at all: it appeared in neither the "Unreached"
                    // report nor the templates one, so it read "Other: 5 Artikel" in every locale.
                    trf(
                        "editor.editor_assist_proposal_result.category_value",
                        "{category}: {value}",
                        "category" to category.displayName(),
                        "value" to form,
                    )
                },
            )
        }
        // Why the agent chose this, in its own words. Secondary text rather than a heading: it is
        // context for the decision, not the thing being decided.
        proposal.rationale?.takeIf(String::isNotBlank)?.let { rationale ->
            Div(Modifier.attr("data-editor-assist-evidence", "rationale").toAttrs()) {
                Text(rationale)
            }
        }
        if (proposal.stale) {
            Div(
                Modifier
                    .attr("data-editor-assist-evidence", "stale")
                    .toAttrs(),
            ) {
                Text(
                    t(
                        "editor.editor_assist_proposal_result.the_source_changed_after_this_proposal",
                        "The source changed after this proposal. Compare it with the active source.",
                    ),
                )
                if (state.capability.canWriteTranslations) {
                    Text(" ")
                    Text(
                        t(
                            "editor.editor_assist_proposal_result.edit_the_target_directly_to_accept",
                            "Edit the target directly to accept a changed version.",
                        ),
                    )
                }
            }
        }
        if (state.capability.canWriteTranslations) {
            val acceptAnnouncement = tf(
                "editor.editor_assist_proposal_result.accept_suggestion_from",
                "Accept proposal {ordinal} from {provenance}",
                "ordinal" to result.shortcutNumber.toString(),
                "provenance" to actionProvenance,
            )
            val rejectAnnouncement = tf(
                "editor.editor_assist_proposal_result.reject_suggestion_from",
                "Reject proposal {ordinal} from {provenance}",
                "ordinal" to result.shortcutNumber.toString(),
                "provenance" to actionProvenance,
            )
            Div(Modifier.attr("data-editor-assist-actions", "true").toAttrs()) {
                LKbd("Alt+${result.shortcutNumber}")
                LButton(
                    label = t("editor.editor_assist_proposal_result.accept", "Accept"),
                    onClick = { state.decideProposal(row.key.id, accept = true) },
                    modifier = Modifier
                        .editorAssistWrappingAction()
                        .attr("id", assistResultActionId(result.shortcutNumber, "accept"))
                        .attr(EDITOR_ASSIST_RESULT_IDENTITY_ATTRIBUTE, result.actionIdentity()),
                    size = LButtonSize.SM,
                    enabled = pending == null || accepting,
                    loading = accepting,
                    accessibleLabel = acceptAnnouncement,
                )
                LButton(
                    label = t("editor.editor_assist_proposal_result.reject", "Reject"),
                    onClick = { state.decideProposal(row.key.id, accept = false) },
                    modifier = Modifier
                        .editorAssistWrappingAction()
                        .attr("id", assistResultActionId(result.shortcutNumber, "reject"))
                        .attr(EDITOR_ASSIST_RESULT_IDENTITY_ATTRIBUTE, result.actionIdentity()),
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                    enabled = pending == null || rejecting,
                    loading = rejecting,
                    accessibleLabel = rejectAnnouncement,
                )
            }
        }
    }
}

@Composable
internal fun AssistOrdinal(ordinal: Int) {
    Span(
        Modifier
            .attr("data-editor-assist-ordinal-label", "true")
            .attr("aria-hidden", "true")
            .toAttrs(),
    ) {
        Text(ordinal.toString())
    }
}
